/**
 * Decisions Tab Component for Meeting Detail
 * Displays and manages decisions made during the meeting
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  GitBranch, Plus, User, Calendar, CheckCircle2, XCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DecisionsTab = ({ 
  meetingId, 
  decisions, 
  token,
  onDecisionsUpdate 
}) => {
  const [decisionDialog, setDecisionDialog] = useState(false);
  const [newDecision, setNewDecision] = useState({
    title: '',
    description: '',
    status: 'approved',
    impact: 'medium'
  });
  const [saving, setSaving] = useState(false);

  const handleCreateDecision = async () => {
    if (!newDecision.title.trim()) {
      toast.error('Decision title is required');
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${API}/api/meeting-intelligence/decisions?token=${token}`,
        {
          ...newDecision,
          meeting_id: meetingId
        }
      );
      
      toast.success('Decision recorded');
      setDecisionDialog(false);
      setNewDecision({
        title: '',
        description: '',
        status: 'approved',
        impact: 'medium'
      });
      
      if (onDecisionsUpdate) onDecisionsUpdate();
    } catch (err) {
      console.error('Failed to create decision:', err);
      toast.error(err.response?.data?.detail || 'Failed to record decision');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return null;
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'muted';
      default: return 'outline';
    }
  };

  return (
    <>
      <Card data-testid="decisions-tab">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="h-5 w-5 text-purple-600" />
                Decisions
              </CardTitle>
              <CardDescription>
                Key decisions made during the meeting
              </CardDescription>
            </div>
            <Button onClick={() => setDecisionDialog(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Decision
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {decisions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <GitBranch className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No decisions recorded</p>
              <p className="text-sm">Run AI analysis to extract decisions</p>
            </div>
          ) : (
            <div className="space-y-4">
              {decisions.map((decision) => (
                <div 
                  key={decision.id}
                  className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {getStatusIcon(decision.status)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{decision.title}</span>
                        <Badge variant={getImpactColor(decision.impact)}>
                          {decision.impact} impact
                        </Badge>
                        <Badge variant={decision.status === 'approved' ? 'success' : 'destructive'}>
                          {decision.status}
                        </Badge>
                      </div>
                      
                      {decision.description && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {decision.description}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        {decision.owner_name && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Owner: {decision.owner_name}
                          </span>
                        )}
                        {decision.created_at && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(decision.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Decision Dialog */}
      <Dialog open={decisionDialog} onOpenChange={setDecisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Decision</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="decision-title">Decision</Label>
              <Input
                id="decision-title"
                value={newDecision.title}
                onChange={(e) => setNewDecision(prev => ({ ...prev, title: e.target.value }))}
                placeholder="What was decided?"
              />
            </div>
            
            <div>
              <Label htmlFor="decision-description">Context/Rationale</Label>
              <Textarea
                id="decision-description"
                value={newDecision.description}
                onChange={(e) => setNewDecision(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Why was this decision made?"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select
                  value={newDecision.status}
                  onValueChange={(value) => setNewDecision(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Impact</Label>
                <Select
                  value={newDecision.impact}
                  onValueChange={(value) => setNewDecision(prev => ({ ...prev, impact: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateDecision} disabled={saving}>
              {saving ? 'Recording...' : 'Record Decision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DecisionsTab;
